// Общее для задач: справочники (/tasks/meta), подписи, ошибки, даты.
import { useEffect, useState } from 'react';
import { tasksApi } from '../../services/api';
import { fmtShortDateTime, toMskInput, fromMskInput, parseServer } from '../../utils/time';

export const fmtDT = fmtShortDateTime;
export const toInput = toMskInput;
export const fromInput = fromMskInput;

export const STATUS = {
  NEW: { label: 'Новая', variant: 'info' },
  IN_PROGRESS: { label: 'В работе', variant: 'warning' },
  WAITING: { label: 'Ждём клиента', variant: 'neutral' },
  REVIEW: { label: 'На проверке', variant: 'purple' },
  DONE: { label: 'Сделана', variant: 'success' },
  CANCELED: { label: 'Отменена', variant: 'neutral' },
};
// Колонки канбана слева направо
export const KANBAN = ['NEW', 'IN_PROGRESS', 'WAITING', 'REVIEW', 'DONE'];
export const PRIORITY = {
  URGENT: { label: 'Срочно', variant: 'danger', cls: 'tk-prio--urgent' },
  HIGH: { label: 'Высокий', variant: 'warning', cls: 'tk-prio--high' },
  NORMAL: { label: 'Обычный', variant: 'neutral', cls: '' },
  LOW: { label: 'Низкий', variant: 'neutral', cls: 'tk-prio--low' },
};
export const FREQ = { daily: 'Каждый день', workdays: 'По будням', weekly: 'Раз в неделю', biweekly: 'Раз в 2 недели', monthly: 'Раз в месяц' };
export const WEEKDAYS = ['пн', 'вт', 'ср', 'чт', 'пт', 'сб', 'вс'];
export const KIND = { INTERNAL: 'Внутренняя', CLIENT: 'От клиента', AUTO: 'Система', CHECKLIST: 'Чек-лист' };
export const SHIFT = { DAY: 'дневная', NIGHT: 'ночная' };
export const UNIT = { pcs: 'шт', box: 'короб', hour: 'час', none: '—' };

export function errText(e, fallback = 'Ошибка') {
  const d = e?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return d.map((x) => x.msg || JSON.stringify(x)).join('; ');
  return e?.friendlyMessage || fallback;
}

export function timeAgo(iso) {
  const d = parseServer(iso);
  if (!d) return '';
  const diff = Math.max(0, (Date.now() - d.getTime()) / 1000);
  if (diff < 60) return 'только что';
  if (diff < 3600) return `${Math.floor(diff / 60)} мин назад`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} ч назад`;
  const days = Math.floor(diff / 86400);
  return days === 1 ? 'вчера' : `${days} дн назад`;
}

/** Срок словами: «через 2 ч», «просрочена на 3 дн». */
export function dueText(iso, status) {
  const d = parseServer(iso);
  if (!d) return 'без срока';
  const closed = status === 'DONE' || status === 'CANCELED';
  const diff = (d.getTime() - Date.now()) / 1000;
  const abs = Math.abs(diff);
  const w = abs < 3600 ? `${Math.max(1, Math.round(abs / 60))} мин` : abs < 86400 ? `${Math.round(abs / 3600)} ч` : `${Math.round(abs / 86400)} дн`;
  if (closed) return fmtShortDateTime(iso);
  return diff >= 0 ? `через ${w}` : `просрочена на ${w}`;
}

/** Кто исполнитель — человек, зона или «никто». */
export function assigneeText(t) {
  if (t.assignee_name) return t.assignee_name;
  if (t.assignee_zone) return `зона: ${t.assignee_zone_name || t.assignee_zone}${t.assignee_shift ? ` (${SHIFT[t.assignee_shift]})` : ''}`;
  return 'никто не взял';
}

let _meta = null;
let _promise = null;
export function loadMeta(force) {
  if (_meta && !force) return Promise.resolve(_meta);
  if (!_promise || force) {
    _promise = tasksApi.meta().then((r) => { _meta = r.data; return _meta; }).catch(() => ({ types: [], zones: [], staff: [] }));
  }
  return _promise;
}

/** Справочники формы задачи: типы, зоны, сотрудники, права смотрящего. */
export function useTasksMeta() {
  const [meta, setMeta] = useState(_meta);
  useEffect(() => { let on = true; loadMeta().then((m) => on && setMeta(m)); return () => { on = false; }; }, []);
  return { meta, reload: () => loadMeta(true).then(setMeta) };
}

/** Доски (с колонками). Общий кэш, перечитать — reload(). */
let _boards = null;
export function useBoards() {
  const [boards, setBoards] = useState(_boards || []);
  const load = () => tasksApi.boards().then((r) => { _boards = r.data; setBoards(r.data); }).catch(() => {});
  useEffect(() => { if (!_boards) load(); }, []);  // eslint-disable-line
  return { boards, reload: load };
}

/** Колонки «сводной» доски (все доски вместе) — по системному статусу. */
export const SUMMARY_COLUMNS = KANBAN.map((k, i) => ({ id: k, name: STATUS[k].label, kind: k, position: i, color: null }));

/** В какой колонке показывать задачу на данной доске: своя колонка, если она есть на доске, иначе первая её статуса. */
export function columnOf(task, columns) {
  const kind = task.status === 'CANCELED' ? 'DONE' : task.status;
  if (task.column_id && columns.some((c) => c.id === task.column_id && c.kind === kind)) return task.column_id;
  const c = columns.find((x) => x.kind === kind);
  return c ? c.id : null;
}

/** Исполнители словами: несколько — через запятую. */
export function assigneesText(t) {
  if (t.assignees && t.assignees.length > 1) return t.assignees.map((a) => (a.name || '').split(' ')[0]).join(', ');
  return assigneeText(t);
}

export function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename; document.body.appendChild(a); a.click();
  setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 100);
}

/** Срок по умолчанию для формы: 18:00 МСК завтра. */
export function defaultDueInput(plusDays = 1) {
  const now = new Date(Date.now() + 3 * 3600 * 1000);
  const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + plusDays, 18, 0));
  return d.toISOString().slice(0, 16);
}
